import { getLogger } from '@trimble/logging';
import { UnreachableCaseError } from 'ts-essentials';

/**
 * Structured FeaturesServiceNode debug log buffer.
 * Enable from the browser console: `window.__featuresNodeDebugLogs.enable()`.
 *
 * Filter with `getLogs({ mode: 'state' })` or `getLogs({ search: tileId })`.
 */

export type FeaturesNodeDebugLogLevel = 'debug' | 'warn' | 'info' | 'error';

export type FeaturesNodeDebugLogMode =
  | 'state'
  | 'fetch'
  | 'lifecycle'
  | 'bounds'
  | 'abort'
  | 'error'
  | 'other';

export interface FeaturesNodeDebugLogEntry {
  readonly timestamp: number;
  readonly level: FeaturesNodeDebugLogLevel;
  readonly message: string;
  readonly mode: FeaturesNodeDebugLogMode;
  readonly context?: Record<string, unknown>;
}

export interface FeaturesNodeDebugLogFilter {
  readonly level?: FeaturesNodeDebugLogLevel;
  readonly mode?: FeaturesNodeDebugLogMode;
  readonly search?: string;
}

export interface FeaturesNodeDebugLogSnapshot {
  readonly totalCount: number;
  readonly stateTransitionCount: number;
  readonly fetchCount: number;
  readonly lifecycleCount: number;
  readonly warnCount: number;
  readonly errorCount: number;
  readonly startedAt: number | null;
  readonly elapsedMs: number | null;
}

const DEFAULT_MAX_ENTRIES = 500;

const inferLogMode = (
  level: FeaturesNodeDebugLogLevel,
  message: string
): FeaturesNodeDebugLogMode => {
  if (level === 'error') {
    return 'error';
  }

  if (message.includes('State transition')) {
    return 'state';
  }

  if (message.includes('Fetching and decoding mesh for tile')) {
    return 'fetch';
  }

  if (
    message.includes('Request aborted') ||
    message.includes('aborted as expected')
  ) {
    return 'abort';
  }

  if (message.includes('Transitioning to start because')) {
    return 'lifecycle';
  }

  if (message.startsWith('box')) {
    return 'bounds';
  }

  return 'other';
};

class FeaturesNodeDebugLogsTracker {
  private enabled = false;
  private readonly entries: FeaturesNodeDebugLogEntry[] = [];
  private startedAt: number | null = null;
  private readonly maxEntries: number;

  constructor(maxEntries = DEFAULT_MAX_ENTRIES) {
    this.maxEntries = maxEntries;
  }

  public enable(): void {
    this.enabled = true;
    this.reset();
  }

  public disable(): void {
    this.enabled = false;
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  public reset(): void {
    this.entries.length = 0;
    this.startedAt = performance.now();
  }

  public record(
    level: FeaturesNodeDebugLogLevel,
    message: string,
    context?: Record<string, unknown>
  ): void {
    if (!this.enabled) {
      return;
    }

    if (this.startedAt === null) {
      this.startedAt = performance.now();
    }

    this.entries.push({
      timestamp: performance.now(),
      level,
      message,
      mode: inferLogMode(level, message),
      context,
    });

    if (this.entries.length > this.maxEntries) {
      this.entries.shift();
    }
  }

  public getLogs(
    filter?: FeaturesNodeDebugLogFilter
  ): FeaturesNodeDebugLogEntry[] {
    let results = [...this.entries];

    if (filter?.level) {
      results = results.filter((entry) => entry.level === filter.level);
    }

    if (filter?.mode) {
      results = results.filter((entry) => entry.mode === filter.mode);
    }

    if (filter?.search) {
      const search = filter.search.toLowerCase();
      results = results.filter(
        (entry) =>
          entry.message.toLowerCase().includes(search) ||
          JSON.stringify(entry.context ?? {})
            .toLowerCase()
            .includes(search)
      );
    }

    return results;
  }

  public getSnapshot(): FeaturesNodeDebugLogSnapshot {
    const elapsedMs =
      this.startedAt !== null ? performance.now() - this.startedAt : null;

    return {
      totalCount: this.entries.length,
      stateTransitionCount: this.entries.filter(
        (entry) => entry.mode === 'state'
      ).length,
      fetchCount: this.entries.filter((entry) => entry.mode === 'fetch').length,
      lifecycleCount: this.entries.filter((entry) => entry.mode === 'lifecycle')
        .length,
      warnCount: this.entries.filter((entry) => entry.level === 'warn').length,
      errorCount: this.entries.filter((entry) => entry.level === 'error')
        .length,
      startedAt: this.startedAt,
      elapsedMs,
    };
  }

  public formatSummary(): string {
    const snapshot = this.getSnapshot();

    return (
      `[FeaturesNodeDebugLogs] total=${snapshot.totalCount}, ` +
      `state=${snapshot.stateTransitionCount}, ` +
      `fetch=${snapshot.fetchCount}, ` +
      `lifecycle=${snapshot.lifecycleCount}, ` +
      `warn=${snapshot.warnCount}, ` +
      `error=${snapshot.errorCount}, ` +
      `elapsedMs=${snapshot.elapsedMs?.toFixed(0) ?? 'n/a'}`
    );
  }
}

export const featuresNodeDebugLogs = new FeaturesNodeDebugLogsTracker();

/**
 * Forwards to `@trimble/logging` only when node debug logs are enabled via
 * `window.__featuresNodeDebugLogs.enable()`. Warnings and errors always log.
 */
const buildRecordContext = (
  context: Record<string, unknown> | undefined,
  error: unknown
): Record<string, unknown> | undefined => {
  if (error === undefined) {
    return context;
  }

  return {
    ...context,
    error: error instanceof Error ? error.message : String(error),
  };
};

const forwardToLogger = (
  logger: ReturnType<typeof getLogger>,
  level: FeaturesNodeDebugLogLevel,
  message: string,
  context?: Record<string, unknown>,
  error?: unknown
): void => {
  const args: unknown[] = [message];
  if (context !== undefined) {
    args.push(context);
  }
  if (error !== undefined) {
    args.push(error);
  }

  switch (level) {
    case 'debug':
      logger.debug(...args);
      break;
    case 'warn':
      logger.warn(...args);
      break;
    case 'info':
      logger.info(...args);
      break;
    case 'error':
      logger.error(...args);
      break;
    default:
      throw new UnreachableCaseError(level);
  }
};

export function logFeaturesNodeDebug(
  logger: ReturnType<typeof getLogger>,
  level: FeaturesNodeDebugLogLevel,
  message: string,
  context?: Record<string, unknown>,
  error?: unknown
): void {
  const isEnabled = featuresNodeDebugLogs.isEnabled();
  const recordContext = buildRecordContext(context, error);

  if (isEnabled) {
    featuresNodeDebugLogs.record(level, message, recordContext);
  }

  if (!isEnabled && level !== 'warn' && level !== 'error') {
    return;
  }

  forwardToLogger(logger, level, message, context, error);
}

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- DevTools debug global
    __featuresNodeDebugLogs?: FeaturesNodeDebugLogsTracker;
  }
}

if (typeof window !== 'undefined') {
  window.__featuresNodeDebugLogs = featuresNodeDebugLogs;
}
